import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { get } from '@truefit/http-utils';

/** One side of a fixture, as the game picker renders it. */
export interface ClipFacetTeam {
  id: number;
  abbreviation?: string | null;
  logoUrl?: string | null;
}

export interface ClipFacetSeason {
  id: number;
  name?: string | null;
  /** 'Regular' | 'Post' | 'Pre' — the DB stores the long form. */
  seasonType?: string | null;
  year?: string | null;
  leagueId?: number | null;
}

export interface ClipFacetGame {
  id: number;
  startTimestamp: string;
  seasonId?: number | null;
  homeTeam: ClipFacetTeam;
  awayTeam: ClipFacetTeam;
}

export interface ClipFacetPlayer {
  /** SES tracked-object id — what the row query filters on. */
  id: number;
  displayName: string;
  playerNumber: string;
  playerPosition: string;
  teamId?: number | null;
  headshot?: string | null;
  teamAbbreviation?: string | null;
  teamLogoUrl?: string | null;
  teamColors?: any;
}

export interface ClipLibraryFacets {
  seasons: ClipFacetSeason[];
  games: ClipFacetGame[];
  players: ClipFacetPlayer[];
  eventTypes: string[];
}

const EMPTY: ClipLibraryFacets = { seasons: [], games: [], players: [], eventTypes: [] };

/**
 * The library's dropdown options.
 *
 * Fetched apart from the rows on purpose: the rows are filtered, and building
 * the options out of them would shrink each list to whatever is already
 * selected. These stay fixed for the league, so selecting a filter never
 * removes the option that undoes it.
 */
const useClipLibraryFacetsQuery = (
  leagueId?: number,
  excludeAutomated?: boolean,
): UseQueryResult<ClipLibraryFacets, Error> =>
  useQuery({
    queryKey: ['clips', 'library', 'facets', leagueId ?? null, Boolean(excludeAutomated)],
    queryFn: async () => {
      const search = new URLSearchParams();
      if (leagueId) search.set('leagueId', String(leagueId));
      // Kept in step with the row query: a clip the viewer cannot see must not
      // put a player or a game into a dropdown either.
      if (excludeAutomated) search.set('excludeAutomated', 'true');

      const response = await get<ClipLibraryFacets>(`/v1.0/clips/facets?${search.toString()}`);
      return response.data ?? EMPTY;
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    retry: 2,
    throwOnError: false,
  });

export default useClipLibraryFacetsQuery;
